import type { Elevation } from '@/shared/services/api/types';

/**
 * Preuve que le code de confidentialité vient d'être saisi.
 *
 * Elle vit **en mémoire seulement** : ni disque, ni stockage sécurisé. Fermer
 * l'application la perd, et c'est voulu : ce qui protège l'argent ne doit pas
 * survivre au téléphone posé sur le comptoir. Le serveur la borne à dix
 * minutes ; on la tient pour périmée un peu avant, pour qu'un geste parti à la
 * dernière seconde n'arrive pas après l'heure.
 */
const SAFETY_MARGIN_MS = 15_000;

type Held = { token: string; expiresAt: number };

let held: Held | null = null;
let challenger: (() => Promise<boolean>) | null = null;

export const elevation = {
  /** La preuve à joindre aux requêtes, ou `null` s'il n'y en a pas de valable. */
  token(): string | null {
    if (held && held.expiresAt - SAFETY_MARGIN_MS > Date.now()) {
      return held.token;
    }
    held = null;
    return null;
  },

  isFresh(): boolean {
    return this.token() !== null;
  },

  /** Le serveur vient de remettre une preuve : création, saisie ou réinitialisation. */
  adopt(proof: Elevation): void {
    held = {
      token: proof.elevationToken,
      expiresAt: Date.now() + proof.expiresInSeconds * 1000,
    };
  },

  /** Déconnexion, ou preuve refusée par le serveur. */
  clear(): void {
    held = null;
  },

  /**
   * Branché une fois par l'interface : ouvre la saisie du code et rend `true`
   * quand elle a réussi. Le client HTTP s'en sert pour rejouer une requête que
   * le serveur a refusée faute de preuve.
   */
  onChallenge(handler: (() => Promise<boolean>) | null): void {
    challenger = handler;
  },

  async challenge(): Promise<boolean> {
    return challenger ? challenger() : false;
  },
};
